import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Grid,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import { getAllBatchNameNoPagination } from '../../redux/features/batchSlice';
import { getSkillxBatchToppers, getSkillxBatchLeaderboard } from '../../redux/features/skillxSlice';
import { SX, rankApiStudents } from './leaderboard/leaderboardData';
import TopPerformersRail from './leaderboard/TopPerformersRail';
import LeaderboardTable from './leaderboard/LeaderboardTable';
import LevelLadder from './leaderboard/LevelLadder';

/**
 * Chief-mentor batch leaderboard. Real data over the XP ledger:
 *   - batch list  → getAllBatchNameNoPagination (batchSlice) — the filter only
 *   - toppers     → getSkillxBatchToppers (#1 per batch, one call)
 *   - standings   → getSkillxBatchLeaderboard(batchId)
 * XP and badges come from the API; rank and level are derived on the frontend
 * from xp (as in the student portal).
 */

// The batch list is fetched unpaginated (`limit=no`), so it is however many
// batches the academy has ever run. Capping what the dropdown renders keeps
// opening it cheap — anything past the cap is reached by typing, which is how
// a list this long gets used anyway.
const filterBatchOptions = createFilterOptions({
  limit: 100,
  stringify: (option) => option.course_batch_name || '',
});

/** One hero statistic — icon tile, value, label. */
function HeroStat({ icon, value, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Box
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: `${SX.radius.sm}px`,
          background: 'rgba(255,255,255,0.18)',
          border: '1px solid rgba(255,255,255,0.26)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon icon={icon} width={19} height={19} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
        <Typography noWrap sx={{ fontSize: 11.5, opacity: 0.88 }}>
          {label}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function SkillxBatchLeaderBoard() {
  const dispatch = useDispatch();
  const { batchesAll } = useSelector((state) => state.batch);

  const [selectedBatchId, setSelectedBatchId] = useState(null);

  // Section 1 — the #1 of every batch that has earned, ordered by XP. The API
  // carries the batch name, so the rail does not wait on (or iterate) the full
  // batch list.
  const [toppers, setToppers] = useState([]);
  const [toppersLoading, setToppersLoading] = useState(true);

  // Section 2 — the selected batch's standings.
  const [tableRows, setTableRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  // Standings already fetched this visit, keyed by batch id. Flipping back to a
  // batch paints from here immediately while a refetch revalidates behind it,
  // so comparing batches costs a spinner only the first time.
  const standingsCache = useRef(new Map());

  // 1. batch list + all toppers on mount
  useEffect(() => {
    dispatch(getAllBatchNameNoPagination({ isAllBatchesNeeded: true }));

    let cancelled = false;
    setToppersLoading(true);
    dispatch(getSkillxBatchToppers())
      .unwrap()
      .then((res) => {
        if (!cancelled) setToppers(res?.data?.toppers || []);
      })
      .catch(() => {
        if (!cancelled) setToppers([]);
      })
      .finally(() => {
        if (!cancelled) setToppersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // 2. default the filter to the first batch once the list arrives
  useEffect(() => {
    if (batchesAll?.length && selectedBatchId == null) {
      setSelectedBatchId(batchesAll[0]?.course_batch_id ?? null);
    }
  }, [batchesAll, selectedBatchId]);

  // 3. standings for the selected batch — cache first, then revalidate
  useEffect(() => {
    if (selectedBatchId == null) return undefined;

    let cancelled = false;
    const cached = standingsCache.current.get(selectedBatchId);

    if (cached) {
      setTableRows(cached);
      setTableLoading(false);
    } else {
      setTableRows([]);
      setTableLoading(true);
    }

    dispatch(getSkillxBatchLeaderboard(selectedBatchId))
      .unwrap()
      .then((res) => {
        const ranked = rankApiStudents(res?.data?.students || []);
        standingsCache.current.set(selectedBatchId, ranked);
        if (!cancelled) setTableRows(ranked);
      })
      .catch(() => {
        if (!cancelled && !cached) setTableRows([]);
      })
      .finally(() => {
        if (!cancelled) setTableLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, selectedBatchId]);

  const selectedBatch = useMemo(
    () => batchesAll?.find((b) => b.course_batch_id === selectedBatchId) || null,
    [batchesAll, selectedBatchId],
  );

  // Hero numbers. Batch counts are academy-wide; the rest describe the batch
  // currently in the filter.
  const stats = useMemo(() => {
    const totalXp = tableRows.reduce((sum, r) => sum + r.xp, 0);
    const batches = (batchesAll || []).length;
    return {
      batches,
      earningBatches: toppers.length,
      silentBatches: Math.max(0, batches - toppers.length),
      learners: tableRows.length,
      totalXp,
      leaderLevel: tableRows[0]?.xp > 0 ? tableRows[0].level : null,
    };
  }, [batchesAll, toppers, tableRows]);

  return (
    <GlobalWrapper title="SkillX Batch Leaderboard">
      {/* ---------- hero ---------- */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mb: 3,
          p: { xs: 2.5, md: 3.5 },
          borderRadius: `${SX.radius.lg}px`,
          background: SX.heroGradient,
          color: '#fff',
          boxShadow: SX.shadow,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -70,
            right: -40,
            width: 210,
            height: 210,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.12)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -90,
            right: 120,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.07)',
          }}
        />

        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ position: 'relative' }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: `${SX.radius.sm}px`,
              background: 'rgba(255,255,255,0.20)',
              border: '1px solid rgba(255,255,255,0.28)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon icon="solar:ranking-bold-duotone" width={22} height={22} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800 }}>Batch Leaderboard</Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.9, mt: 0.25 }}>
              Ranked by XP earned across each batch. Levels and badges update as learners earn.
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={2} sx={{ position: 'relative', mt: { xs: 1, md: 1.5 } }}>
          <Grid item xs={6} md={3}>
            <HeroStat
              icon="solar:users-group-rounded-bold-duotone"
              value={`${stats.earningBatches} / ${stats.batches}`}
              label="Batches earning XP"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <HeroStat icon="solar:user-id-bold" value={stats.learners} label="Learners in this batch" />
          </Grid>
          <Grid item xs={6} md={3}>
            <HeroStat
              icon="solar:bolt-bold-duotone"
              value={stats.totalXp.toLocaleString()}
              label="XP earned in this batch"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <HeroStat
              icon={stats.leaderLevel?.icon || 'solar:medal-star-bold-duotone'}
              value={stats.leaderLevel?.name || '—'}
              label="Top level reached"
            />
          </Grid>
        </Grid>
      </Box>

      {/* ---------- section 1: top performers by batch ---------- */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Icon icon="solar:cup-star-bold-duotone" width={20} height={20} color="#D4A017" />
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: SX.text }}>Top Performers by Batch</Typography>
        <Typography sx={{ fontSize: 12, color: SX.textMuted }}>— the #1 learner in every batch</Typography>
      </Stack>

      <TopPerformersRail
        toppers={toppers}
        loading={toppersLoading}
        silentBatchCount={stats.silentBatches}
      />

      {/* ---------- section 2: full standings with batch filter ---------- */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Icon icon="solar:ranking-bold-duotone" width={20} height={20} color={SX.blue} />
            <Typography sx={{ fontSize: 16, fontWeight: 800, color: SX.text }}>Full Standings</Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} sm={6} md={4} sx={{ ml: { md: 'auto' } }}>
          <Autocomplete
            fullWidth
            size="small"
            options={batchesAll || []}
            filterOptions={filterBatchOptions}
            getOptionLabel={(option) => option.course_batch_name || ''}
            isOptionEqualToValue={(option, value) => option.course_batch_id === value?.course_batch_id}
            value={selectedBatch}
            onChange={(e, option) => setSelectedBatchId(option?.course_batch_id ?? null)}
            renderInput={(params) => <TextField {...params} label="Filter by batch" autoComplete="off" />}
          />
        </Grid>
      </Grid>

      <LeaderboardTable
        rows={tableRows}
        loading={tableLoading}
        batchId={selectedBatchId}
        batchName={selectedBatch?.course_batch_name}
      />

      {/* ---------- level reference ---------- */}
      <Box sx={{ mt: 2.5 }}>
        <LevelLadder reached={stats.leaderLevel?.key} />
      </Box>
    </GlobalWrapper>
  );
}
